import React, { useMemo, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Tooltip,
  Chip,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import config from "./chatAssistantConfig";
import { buildCsv, downloadCsv, exportBaseName, textToBase64 } from "./downloadUtils";
import ShareResultDialog from "./ShareResultDialog";

/**
 * Columns whose numbers are identifiers or years, not quantities. These must
 * not get thousands separators - an employee id of 817667 has to read as
 * "817667", never "817,667".
 */
const UNFORMATTED_NUMBER_COLUMN = /(^|\b|_)(id|ids|year|code|zip|pin|phone|week)(\b|_|$)/i;

/** Renders a value for on-screen display. */
function displayValue(value, column) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") {
    if (UNFORMATTED_NUMBER_COLUMN.test(String(column))) return String(value);
    return value.toLocaleString("en-US");
  }
  return String(value);
}

/**
 * Compact result grid for one assistant answer, with a CSV download.
 *
 * `rows` holds the rows shown in the chat (capped server-side by
 * displayRowLimit). `allRows` is the full result set and is only sent when it
 * is larger than the display cap - the CSV always exports the larger of the two
 * so the download is never silently truncated to what happens to be on screen.
 */
export default function ChatResultTable({
  title,
  columns,
  rows,
  allRows,
  rowCount,
  truncated,
  hitCeiling,
}) {
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const exportRows = useMemo(
    () => (allRows && allRows.length ? allRows : rows),
    [allRows, rows]
  );

  const handleDownload = () => {
    downloadCsv(columns, exportRows, title);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  if (!columns || !columns.length) return null;

  return (
    <Box sx={{ mt: 1 }}>
      {/* Header row: title + row count + download */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 0.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {title && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: config.COLORS.primary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={title}
            >
              {title}
            </Typography>
          )}
          <Chip
            size="small"
            label={`${rowCount.toLocaleString("en-US")} row${rowCount === 1 ? "" : "s"}`}
            sx={{ height: 18, fontSize: "0.65rem", flexShrink: 0 }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center" }}><Tooltip title="Email this data"><Button size="small" onClick={() => setShareOpen(true)} startIcon={<MailOutlineRoundedIcon sx={{ fontSize: ".95rem" }} />} sx={{ textTransform: "none", fontSize: ".7rem", minWidth: "auto", color: config.COLORS.primary }}>Share</Button></Tooltip><Tooltip title={copied ? "Downloaded" : `Download all ${exportRows.length.toLocaleString("en-US")} rows as CSV`}>
          <Button
            size="small"
            onClick={handleDownload}
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "0.95rem" }} />}
            sx={{
              textTransform: "none",
              fontSize: "0.7rem",
              py: 0.1,
              px: 0.9,
              minWidth: "auto",
              flexShrink: 0,
              color: config.COLORS.primary,
            }}
          >
            {copied ? "Saved" : "CSV"}
          </Button>
        </Tooltip></Box>
      </Box>

      <TableContainer
        sx={{
          maxHeight: config.TABLE_MAX_HEIGHT,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          bgcolor: "#fff",
        }}
      >
        <Table size="small" stickyHeader sx={{ "& td, & th": { px: 1, py: 0.4 } }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    whiteSpace: "nowrap",
                    bgcolor: "#f1f6fb",
                    color: config.COLORS.primaryDark,
                    borderBottom: `2px solid ${config.COLORS.primary}22`,
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                key={i}
                hover
                sx={{ "&:nth-of-type(odd)": { bgcolor: "#fafcfe" } }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col}
                    sx={{
                      fontSize: "0.68rem",
                      whiteSpace: "nowrap",
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={
                      row[col] === null || row[col] === undefined
                        ? ""
                        : String(row[col])
                    }
                  >
                    {displayValue(row[col], col)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {truncated && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "text.secondary", fontSize: "0.65rem" }}
        >
          Showing the first {rows.length.toLocaleString("en-US")} of{" "}
          {rowCount.toLocaleString("en-US")} rows. Download the CSV for the full set.
        </Typography>
      )}
      {hitCeiling && (
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.4, color: "warning.main", fontSize: "0.65rem" }}
        >
          This result reached the maximum row limit, so it may be incomplete. Try
          adding a filter to narrow it down.
        </Typography>
      )}
      <ShareResultDialog open={shareOpen} onClose={() => setShareOpen(false)} title={title || "MatchIQ result"} resultType="Data" buildAttachment={async () => ({ filename: `${exportBaseName(title)}.csv`, contentType: "text/csv", base64: textToBase64(buildCsv(columns, exportRows)) })} />
    </Box>
  );
}
